import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import {
  ActivatedRoute,
  ActivatedRouteSnapshot,
  NavigationEnd,
  Router,
} from '@angular/router';
import { Title } from '@angular/platform-browser';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, map, startWith } from 'rxjs';
import { OutkeepPermission } from '@outkeep/shared';
import {
  AppRoutePaths,
  NavGroup,
  NavItem,
  PageContext,
  REQUEST_LIST_ROUTE_SEGMENTS,
} from '@core/types';
import { OutkeepPermissionService } from '@core/services/permission/outkeep-permission.service';

@Injectable({ providedIn: 'root' })
export class NavigationService {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly titleService = inject(Title);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(OutkeepPermissionService);

  public $navbarHeader = signal<string>('');

  readonly menuGroups: NavGroup[] = [
    {
      groupLabel: 'group_requests',
      items: [
        {
          label: 'all',
          icon: '@tui.list',
          link: AppRoutePaths.AllRequests,
          permission: OutkeepPermission.LogistOn,
        },
      ],
    },
    {
      groupLabel: 'group_time_management',
      items: [
        {
          label: 'requests_distribution',
          icon: '@tui.between-horizontal-start',
          link: AppRoutePaths.LogistTMRequestsDistribution,
          permission: OutkeepPermission.LogistTimeManagementOn,
        },
        {
          label: 'requests_registry',
          icon: '@tui.grid-2x2-plus',
          link: AppRoutePaths.LogistTMRequestsRegistry,
          permission: OutkeepPermission.LogistTimeManagementOn,
        },
      ],
    },
    {
      items: [
        {
          label: 'warehouse',
          icon: '@tui.shelving-unit',
          link: AppRoutePaths.Warehouse,
          permission: [
            OutkeepPermission.Warehouse,
            OutkeepPermission.WarehouseOn,
          ],
        },
        {
          label: 'payroll',
          icon: '@tui.dollar-sign',
          link: AppRoutePaths.Payroll,
          permission: [OutkeepPermission.Payroll, OutkeepPermission.PayrollOn],
        },
        {
          label: 'docflow',
          icon: '@tui.file-text',
          link: AppRoutePaths.Docflow,
          permission: OutkeepPermission.LogistDocFlowOn,
        },
      ],
    },
  ];

  public isRequestsRouteActive(link: string): boolean {
    return this.router.url.split('?')[0] === link;
  }

  public groupHasVisibleItems(items: NavItem[]): boolean {
    return items.some(item =>
      this.permissionService.$hasPermission(item.permission)(),
    );
  }

  /** Единственный источник истины о текущей странице */
  public $pageContext = signal<PageContext>(PageContext.RequestList);

  /** Мы «углубились» от главного списка — показывать хедер с кнопкой «назад» */
  public $canGoBack = computed(
    () => this.$pageContext() !== PageContext.RequestList,
  );

  /** Страница конкретной заявки — показывать кнопку смены статуса */
  public $isRequestPage = computed(
    () => this.$pageContext() === PageContext.RequestPage,
  );

  /** Страница профиля */
  public $isProfilePage = computed(
    () => this.$pageContext() === PageContext.ProfilePage,
  );

  constructor() {
    this.initializeNavigation();
  }

  private initializeNavigation(): void {
    this.router.events
      .pipe(
        startWith(new NavigationEnd(0, this.router.url, this.router.url)),
        filter(event => event instanceof NavigationEnd),
        map(() => {
          let route = this.route.snapshot;

          while (route.firstChild) {
            route = route.firstChild;
          }

          return route;
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((route: ActivatedRouteSnapshot) => {
        const title =
          route.title?.split('X-Outkeep | ')[1] ||
          this.titleService.getTitle().split('X-Outkeep | ')[1];

        this.$navbarHeader.set(title || '');

        this.$pageContext.set(this.resolvePageContext(route));
      });
  }

  private resolvePageContext(route: ActivatedRouteSnapshot): PageContext {
    // Идём от листового роута вверх по дереву предков,
    // чтобы найти нужный сегмент — листовой может быть 'summary', 'devices' и т.д.
    let cursor: ActivatedRouteSnapshot | null = route;

    while (cursor) {
      const segment = cursor.routeConfig?.path;

      if (segment && REQUEST_LIST_ROUTE_SEGMENTS.includes(segment)) {
        return PageContext.RequestList;
      }
      if (segment === ':requestNumber') {
        return PageContext.RequestPage;
      }
      if (segment === AppRoutePaths.Profile) {
        return PageContext.ProfilePage;
      }

      cursor = cursor.parent;
    }

    return PageContext.Other;
  }
}
